import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface NewFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (title: string) => Promise<void>;
}

export default function NewFolderModal({ isOpen, onClose, onSubmit }: NewFolderModalProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTitle("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(trimmed);
      onClose();
    } catch (err) {
      console.error("[NewFolderModal] submit error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-sm bg-black/60 transition-opacity"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-xl bg-white dark:bg-[hsl(var(--bg2))] border border-[hsl(var(--border))] p-6 transition-all duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-4"
          style={{
            background: "linear-gradient(135deg, rgba(109,90,255,.15), rgba(0,229,160,.12))",
            border: "1px solid hsl(var(--border))",
          }}
        >
          📁
        </div>

        {/* Title */}
        <h3
          className="text-xl font-semibold mb-4 font-[Instrument_Serif]"
          style={{ color: "hsl(var(--foreground))" }}
        >
          {t("folder.newFolder")}
        </h3>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("folder.namePlaceholder")}
            autoFocus
            className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent transition-all mb-5 text-sm"
          />

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] font-medium text-sm hover:border-[hsl(var(--primary))] transition-colors"
            >
              {t("folder.cancel")}
            </button>
            <button
              type="submit"
              disabled={!title.trim() || isSubmitting}
              className="flex-1 py-3 px-4 rounded-xl border-0 text-white font-semibold text-sm bg-[hsl(var(--primary))] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isSubmitting ? "…" : t("folder.createFolder")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
